/**
 * Display helpers of the inventory report workbench (batch 8 T14c, #3036). Presentation only.
 *
 * The API sends exact money as text with four decimals of a cent (`"1402.0000"`, `"-0.4900"`) and, where the core
 * rounded once at its report boundary, an integer count of whole cents. Neither is ever passed through `Number`:
 * the text is read as a `bigint` and shown with the tested `costMoney` formatters, the integer cents as they are.
 * Nothing here adds, subtracts or recomputes a figure; a text that is not exact is shown as it arrived, never as 0.
 * All amounts are net EUR (the ledger holds no VAT).
 */
import { formatEuroFromCents, formatEuroFromCents4 } from '@/features/inventory/costing/costMoney';

const CENTS4_PER_CENT = 10_000n;

/** The API's signed cents text (`"-12.3400"`, `"0"`) as 0.0001-cent units; `null` when it is not an exact decimal. */
export function parseSignedCents4(text: string): bigint | null {
  const match = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(text.trim());
  if (!match) return null;
  const magnitude = BigInt(match[2] ?? '0') * CENTS4_PER_CENT + BigInt((match[3] ?? '').padEnd(4, '0'));
  return match[1] === '-' ? -magnitude : magnitude;
}

/** Exact euro text of the API's cents text (never rounded); the raw text when it is not an exact decimal. */
export function formatExactEuro(cents4Text: string, language: string): string {
  const cents4 = parseSignedCents4(cents4Text);
  if (cents4 === null) return cents4Text;
  // The sign goes in front of the whole amount (`-€0.05`, `-0,05 €`), not between the symbol and the digits.
  return cents4 < 0n ? `-${formatEuroFromCents4(-cents4, language)}` : formatEuroFromCents4(cents4, language);
}

/** Euro text of whole cents the server rounded once. */
export function formatRoundedEuro(cents: number, language: string): string {
  if (!Number.isSafeInteger(cents)) return String(cents);
  return cents < 0 ? `-${formatEuroFromCents(-cents, language)}` : formatEuroFromCents(cents, language);
}

export interface MoneyView {
  /** What to show first: the once-rounded figure when the server sent one, otherwise the exact text. */
  readonly primary: string;
  /** The exact figure, only when it adds something beyond `primary`. */
  readonly exact: string | null;
}

/** Money from an exact text and, when the server rounded it once, that rounded integer. */
export function moneyView(cents4Text: string, cents: number | null | undefined, language: string): MoneyView {
  const exact = formatExactEuro(cents4Text, language);
  if (cents === null || cents === undefined) return { primary: exact, exact: null };
  const primary = formatRoundedEuro(cents, language);
  return { primary, exact: primary === exact ? null : exact };
}

/** A count, a ratio or a percentage figure the server already rounded: shown in the user's locale, up to four decimals. */
export function formatFigure(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 4 }).format(value);
}

export function formatPercent(value: number, language: string): string {
  return `${formatFigure(value, language)}\u00a0%`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * An instant the server sent (UTC text) in the tenant zone the report states, or in UTC when the report states none.
 * Never the browser's zone. A plain `YYYY-MM-DD` is a tenant-local calendar date and is shown as it is.
 */
export function formatInstant(text: string, timeZone: string | null, language: string): string {
  if (DATE_ONLY.test(text)) return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  const zone = timeZone ?? 'UTC';
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: 'short', timeStyle: 'long', timeZone: zone }).format(date);
  } catch {
    return text;
  }
}

/**
 * An amount of ANY currency from the API's exact text (a supplier list price keeps the currency it was quoted in; no
 * exchange rate is ever applied). EUR gets the euro formatting; another currency shows the amount and its code.
 */
export function formatQuotedAmount(cents4Text: string, currency: string, language: string): string {
  if (currency.toUpperCase() === 'EUR') return formatExactEuro(cents4Text, language);
  const cents4 = parseSignedCents4(cents4Text);
  if (cents4 === null) return `${cents4Text} ${currency}`;
  const negative = cents4 < 0n;
  const abs = negative ? -cents4 : cents4;
  const major = abs / 1_000_000n;
  let fraction = (abs % 1_000_000n).toString().padStart(6, '0');
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const separator = language.startsWith('en') ? '.' : ',';
  const whole = new Intl.NumberFormat(language, { useGrouping: true }).format(major);
  return `${negative ? '-' : ''}${whole}${separator}${fraction}\u00a0${currency}`;
}
